import { useChangeEmail } from "hooks/useChangeEmail";
import { LoadingButton } from "@mui/lab";
import {
    Box, Button, FormControl, Stack, TextField, Typography, useTheme,
    Dialog, DialogTitle, DialogContent, DialogActions, IconButton, InputAdornment, OutlinedInput, InputLabel
} from "@mui/material";
import { useCallback, useContext, useState } from "react";
import { useTranslation } from "react-i18next";
import { emailRegex } from "constants/regex";
import { UserProfileViewContext } from "context/userProfile/UserProfileViewContext";
import { UserRoles } from "constants/userRoles";
import { truncateString } from "helpers/stringHelper";
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import { CancelButton, SaveButton, StyledFormControl, ErrorTypography, DialogCancelButton } from 'components/views/profile/ChangeEmailSection/ChangeEmailSection.styles';
import { styled } from '@mui/material/styles';

const MobileChangeEmailButton = styled(Button)(({ theme }) => ({
    marginLeft: 'auto',
    flexShrink: 0,
    color: theme.palette.primary.main,
    borderColor: theme.palette.primary.main,
    borderRadius: '20px',
    width: '10rem',
    height: '40px',
}));

export const MobileChangeEmailSection = (props: { email: string }) => {
    const theme = useTheme();
    const { t } = useTranslation();
    const [changingEmail, setChangingEmail] = useState(false);
    const [newEmail, setNewEmail] = useState(props.email);
    const [emailError, setEmailError] = useState('');
    const [showPasswordDialog, setShowPasswordDialog] = useState(false);
    const [password, setPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [passwordError, setPasswordError] = useState('');
    const { changeEmail, changeEmailPending } = useChangeEmail();
    const { refreshProfile, isProfileOfLoggedUser, user } = useContext(UserProfileViewContext);

    const isValidEmail = (email: string): boolean => emailRegex.test(email);

    const validateEmail = useCallback(() => {
        if (!isValidEmail(newEmail)) {
            setEmailError(t('profile.invalidEmail'));
            return false;
        }
        setEmailError('');
        return true;
    }, [newEmail, t]);

    const handleSave = useCallback(async () => {
        if (validateEmail()) {
            setShowPasswordDialog(true);
        }
    }, [validateEmail]);

    const handlePasswordConfirm = useCallback(async () => {
        if (!password) {
            setPasswordError(t('loginPage.errors.passwordRequired'));
            return;
        }
        setPasswordError('');

        const resp = await changeEmail({
            newEmail,
            userId: isProfileOfLoggedUser ? undefined : user?.id,
            password
        });

        if (resp?.success) {
            setChangingEmail(false);
            setShowPasswordDialog(false);
            setPassword('');
            refreshProfile();
        }
    }, [password, t, changeEmail, newEmail, isProfileOfLoggedUser, user?.id, refreshProfile]);

    const handleClickShowPassword = () => setShowPassword(!showPassword);
    const handleMouseDownPassword = (event: any) => event.preventDefault();

    return (
        <>
            <Stack width='100%' direction='row' alignItems='center'>
                {changingEmail
                    ? <>
                        <Box flex={1}>
                            <FormControl variant="outlined">
                                <TextField
                                    id="newEmail"
                                    label={t('general.newEmail')}
                                    variant="standard"
                                    value={newEmail}
                                    onChange={(e) => setNewEmail(e.target.value)}
                                />
                            </FormControl>
                            {emailError && <Typography color='red'>{emailError}</Typography>}
                        </Box>
                        <Box>
                            <CancelButton variant='outlined' color='primary' onClick={() => setChangingEmail(false)}>
                                {t('profile.cancel')}
                            </CancelButton>
                            {(user?.role !== UserRoles.ADMIN || isProfileOfLoggedUser) && (
                                <SaveButton loading={changeEmailPending} variant='outlined' color='primary' onClick={handleSave}>
                                    {t('profile.save')}
                                </SaveButton>
                            )}
                        </Box>
                    </>
                    : <>
                        <Stack>
                            <Typography variant="caption" color={theme.palette.text.secondary}>
                                {t('profile.emailLogin')}
                            </Typography>
                            <Typography variant="body2" fontWeight='500'>
                                {truncateString(props.email, 22)}
                            </Typography>
                        </Stack>
                        <MobileChangeEmailButton variant='outlined' color='info' onClick={() => setChangingEmail(true)}>
                            {t('profile.changeEmail')}
                        </MobileChangeEmailButton>
                    </>
                }
            </Stack>

            <Dialog open={showPasswordDialog} onClose={() => setShowPasswordDialog(false)}>
                <DialogTitle>{t('profile.confirmPassword')}</DialogTitle>
                <DialogContent>
                    <StyledFormControl variant="outlined">
                        <InputLabel htmlFor="password">{t('general.password')}</InputLabel>
                        <OutlinedInput
                            id="password"
                            label={t('general.password')}
                            type={showPassword ? 'text' : 'password'}
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            endAdornment={
                                <InputAdornment position="end">
                                    <IconButton onClick={handleClickShowPassword} onMouseDown={handleMouseDownPassword} edge="end">
                                        {showPassword ? <VisibilityOff /> : <Visibility />}
                                    </IconButton>
                                </InputAdornment>
                            }
                        />
                        {passwordError && <ErrorTypography color='red'>{passwordError}</ErrorTypography>}
                    </StyledFormControl>
                </DialogContent>
                <DialogActions>
                    <DialogCancelButton onClick={() => setShowPasswordDialog(false)} variant="outlined" color="primary">
                        {t('profile.cancel')}
                    </DialogCancelButton>
                    <LoadingButton loading={changeEmailPending} onClick={handlePasswordConfirm} variant="outlined" color="primary">
                        {t('profile.confirm')}
                    </LoadingButton>
                </DialogActions>
            </Dialog>
        </>
    );
}
